import React, { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ShieldCheck, Trash2, Plus, Search, Check, Settings, Save, History, Calendar } from "lucide-react";

const FSCSettingPage = () => {
  const [fscList, setFscList] = useState([]); // List Customer Bebas
  const [historyList, setHistoryList] = useState([]); // List History Angka
  
  // State Input Global FSC
  const [fscValue, setFscValue] = useState(0);
  const [period, setPeriod] = useState(""); // Format: "2026-02"

  // State Modal & Search
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedCust, setSelectedCust] = useState("");
  const [searchQuery, setSearchQuery] = useState("");
  const [searchResults, setSearchResults] = useState([]); 
  const [isLoading, setIsLoading] = useState(true);

  const API_URL = "/api";

  useEffect(() => {
    // Set default month ke bulan ini (YYYY-MM)
    const now = new Date();
    const currentMonth = now.toISOString().slice(0, 7); // "2026-02"
    setPeriod(currentMonth);

    fetchCurrentSetting();
    fetchHistory();
    fetchFSCList();
  }, []);

  // Live Search Effect
  useEffect(() => {
    const timer = setTimeout(() => {
      if (isModalOpen) performServerSearch(searchQuery);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery, isModalOpen]);

  // --- API CALLS ---
  const fetchCurrentSetting = async () => {
    try {
        const res = await axios.get(`${API_URL}/settings/global`);
        setFscValue(res.data.fsc_percent);
        // Jika ada data history terakhir, pakai periode itu
        if(res.data.period) setPeriod(res.data.period); 
    } catch (error) { console.error(error); }
  };

  const fetchHistory = async () => {
    try {
        const res = await axios.get(`${API_URL}/settings/history`);
        setHistoryList(res.data);
    } catch (error) { console.error(error); }
  };

  const fetchFSCList = async () => {
    try {
      const res = await axios.get(`${API_URL}/fsc/settings`);
      setFscList(res.data);
    } catch (error) { console.error(error); } 
    finally { setIsLoading(false); }
  };

  // --- ACTIONS ---
  const handleSaveGlobal = async () => {
    if(!period) return toast.error("Pilih periode bulan dulu!");
    try {
        await axios.post(`${API_URL}/settings/global`, { 
            period: period,
            fsc_percent: parseFloat(fscValue) 
        });
        toast.success(`FSC untuk periode ${period} berhasil disimpan!`);
        fetchHistory(); // Refresh tabel history
    } catch (error) {
        toast.error("Gagal menyimpan setting.");
    }
  };

  // ... (Search & Delete logic sama seperti sebelumnya) ...
  const performServerSearch = async (keyword) => {
    try {
      const res = await axios.get(`${API_URL}/customers`, { params: { search: keyword, limit: 20 } });
      setSearchResults(res.data.data ? res.data.data : res.data);
    } catch (error) {}
  };
  const handleAdd = async () => {
    if (!selectedCust) return;
    try {
      await axios.post(`${API_URL}/fsc/settings`, { customer_db_id: selectedCust });
      toast.success("Berhasil didaftarkan BEBAS FSC");
      setIsModalOpen(false); setSearchQuery(""); setSelectedCust(""); fetchFSCList();
    } catch (error) { toast.error("Gagal menambah."); }
  };
  const handleDelete = async (id) => {
    if(!window.confirm("Hapus pengecualian?")) return;
    try { await axios.delete(`${API_URL}/fsc/settings/${id}`); fetchFSCList(); } catch (error) {}
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      
      {/* HEADER UTAMA */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        
        {/* KOLOM KIRI: INPUT SETTING */}
        <div className="md:col-span-2 bg-white p-6 rounded-xl border shadow-sm">
            <h2 className="text-xl font-bold text-gray-900 flex items-center gap-2 mb-4">
                <Settings className="text-blue-600" /> Update FSC Bulanan
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Input Bulan */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                    <label className="text-xs font-bold text-gray-500 uppercase flex items-center gap-2 mb-2">
                        <Calendar size={14}/> Periode Bulan
                    </label>
                    <input 
                        type="month" 
                        className="w-full bg-white border p-2 rounded font-medium outline-blue-600"
                        value={period}
                        onChange={(e) => setPeriod(e.target.value)}
                    />
                    <p className="text-xs text-gray-400 mt-2">Pilih bulan berlakunya tarif.</p>
                </div>

                {/* Input Persen */}
                <div className="bg-gray-50 p-4 rounded-lg border">
                    <label className="text-xs font-bold text-gray-500 uppercase mb-2 block">Persentase (%)</label>
                    <div className="flex items-center gap-2">
                        <input 
                            type="number" step="0.1"
                            className="text-3xl font-bold text-blue-600 bg-transparent border-b-2 border-blue-200 focus:border-blue-600 outline-none w-full"
                            value={fscValue}
                            onChange={(e) => setFscValue(e.target.value)}
                        />
                        <span className="text-2xl font-bold text-gray-400">%</span>
                    </div>
                </div>
            </div>

            <button 
                onClick={handleSaveGlobal}
                className="w-full mt-4 bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg font-bold flex justify-center items-center gap-2 transition"
            >
                <Save size={18} /> Simpan Tarif
            </button>
        </div>

        {/* KOLOM KANAN: HISTORY LOG */}
        <div className="bg-white p-0 rounded-xl border shadow-sm overflow-hidden flex flex-col">
            <div className="p-4 border-b bg-gray-50">
                <h3 className="font-bold text-gray-800 flex items-center gap-2">
                    <History size={18} className="text-gray-500"/> Riwayat Tarif
                </h3>
            </div>
            <div className="overflow-y-auto max-h-[250px]">
                <table className="w-full text-sm text-left">
                    <thead className="text-xs text-gray-500 bg-gray-50 uppercase">
                        <tr>
                            <th className="px-4 py-2">Periode</th>
                            <th className="px-4 py-2 text-right">Rate</th>
                            <th className="px-4 py-2">Update</th>
                        </tr>
                    </thead>
                    <tbody className="divide-y">
                        {historyList.map((h) => (
                            <tr key={h.id} className={h.period === period ? "bg-blue-50" : ""}>
                                <td className="px-4 py-3 font-medium">{h.period}</td>
                                <td className="px-4 py-3 text-right font-bold text-blue-600">{h.percentage}%</td>
                                <td className="px-4 py-3 text-xs text-gray-400">
                                    {new Date(h.updated_at).toLocaleDateString()}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
      </div>

      <hr className="border-gray-200" />

      {/* SECTION 2: EXEMPTION LIST (DAFTAR BEBAS FSC) */}
      <div>
          <div className="flex justify-between items-center bg-white p-6 rounded-t-xl border-x border-t shadow-sm">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <ShieldCheck className="text-green-600" /> Daftar Bebas FSC
              </h1>
              <p className="text-gray-500 mt-1">Customer ini <b>GRATIS (0%)</b>, tidak ikut tarif history di atas.</p>
            </div>
            <button onClick={() => setIsModalOpen(true)} className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-medium flex items-center gap-2">
                <Plus size={18} /> Tambah Pengecualian
            </button>
          </div>

          <div className="bg-white border rounded-b-xl shadow-sm overflow-hidden">
            <table className="w-full text-left">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="px-6 py-4 font-bold text-gray-700">Customer ID</th>
                  <th className="px-6 py-4 font-bold text-gray-700">Nama Perusahaan</th>
                  <th className="px-6 py-4 font-bold text-gray-700">Status</th>
                  <th className="px-6 py-4 text-right font-bold text-gray-700">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y">
                {fscList.length === 0 ? (
                    <tr><td colSpan="4" className="text-center py-8 text-gray-500">Belum ada customer bebas FSC.</td></tr>
                ) : (
                    fscList.map(item => (
                        <tr key={item.id} className="hover:bg-gray-50">
                            <td className="px-6 py-4 font-mono text-gray-600">{item.customer_id}</td>
                            <td className="px-6 py-4 font-medium text-gray-900">{item.company}</td>
                            <td className="px-6 py-4">
                                <span className="bg-green-100 text-green-800 text-xs px-2 py-1 rounded-full font-bold flex w-fit items-center gap-1">
                                    <ShieldCheck size={12}/> Bebas FSC
                                </span>
                            </td>
                            <td className="px-6 py-4 text-right">
                                <button onClick={() => handleDelete(item.id)} className="text-red-500 hover:bg-red-50 p-2 rounded-lg"><Trash2 size={18}/></button>
                            </td>
                        </tr>
                    ))
                )}
              </tbody>
            </table>
          </div>
      </div>

      {/* MODAL SAMA SEPERTI SEBELUMNYA (SEARCH SERVER-SIDE) */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-xl w-full max-w-lg shadow-2xl overflow-hidden flex flex-col max-h-[80vh]">
                <div className="p-5 border-b bg-green-50">
                    <h3 className="font-bold text-lg text-green-900">Tambah Customer Bebas FSC</h3>
                </div>
                <div className="p-4 border-b">
                    <input type="text" placeholder="Cari..." className="w-full px-4 py-3 border rounded-lg outline-green-500"
                        value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} autoFocus />
                </div>
                <div className="flex-1 overflow-y-auto p-2 space-y-1 bg-white min-h-[200px]">
                    {searchResults.map(c => (
                        <div key={c.id} onClick={() => setSelectedCust(c.id)} className={`p-3 rounded-lg cursor-pointer flex justify-between items-center ${selectedCust === c.id ? 'bg-green-50 ring-1 ring-green-500' : 'hover:bg-gray-50'}`}>
                            <div><div className="font-bold text-gray-800">{c.company}</div><div className="text-xs text-gray-500">{c.customer_id}</div></div>
                            {selectedCust === c.id && <Check className="text-green-600" size={20} />}
                        </div>
                    ))}
                </div>
                <div className="p-5 border-t bg-gray-50 flex justify-end gap-3">
                    <button onClick={() => setIsModalOpen(false)} className="px-5 py-2 text-gray-600 hover:bg-gray-100 rounded-lg">Batal</button>
                    <button onClick={handleAdd} disabled={!selectedCust} className="px-5 py-2 bg-green-600 text-white rounded-lg font-bold">Simpan</button>
                </div>
            </div>
        </div>
      )}
    </div>
  );
};

export default FSCSettingPage;